<template>
  <div class="cancel-statements-editor">
    <RegionMismatchAdvisory v-if="hasRegionMismatch" class="mb-3" />

    <!-- Any AIB is selected but the shared purpose does not yet use {address}: a non-blocking hint
         that the placeholder is available. -->
    <div v-if="shouldSuggestAddressPlaceholder" class="advisory-note mb-3">
      <FontAwesomeIcon
        :icon="['far', 'circle-info']"
        class="flex-shrink-0 mt-1"
      />
      <div>{{ $t("go_cancel.any_aib_address_hint") }}</div>
    </div>

    <UnsupportedRegistryNotice
      v-if="unsupportedRegistryEntries.length > 0"
      class="mb-3"
      :entries="unsupportedRegistryEntries"
    />

    <!-- Cancellation Statements Section -->
    <div class="row mb-3">
      <div class="col">
        <div class="d-flex justify-content-between align-items-end mb-3">
          <h4 class="font-serif mb-0">
            {{ $t("go_cancel.statements_title") }}
          </h4>
          <button
            v-if="!isEditMwhOnly"
            class="btn btn-primary"
            :disabled="isAtStatementLimit"
            @click="addCancelStatement"
          >
            <i class="bi bi-plus-circle"></i>
            {{ $t("go_cancel.add_statement_button") }}
          </button>
        </div>

        <div v-if="isAtStatementLimit" class="error-note mb-3">
          <FontAwesomeIcon icon="exclamation-triangle" class="flex-shrink-0" />
          <span>{{
            $t("go_cancel.max_statements_reached", {
              max: MAX_CANCEL_STATEMENTS,
            })
          }}</span>
        </div>

        <div v-if="props.truncatedWarning" class="error-note mb-3">
          <FontAwesomeIcon icon="exclamation-triangle" class="flex-shrink-0" />
          <span>{{ props.truncatedWarning }}</span>
        </div>

        <div class="card cancel-statements-card">
          <div class="position-relative">
            <div
              ref="tableScrollRef"
              class="table-responsive compact-table-container"
            >
              <DataTable
                v-if="cancelRows.length > 0"
                :value="cancelRows"
                dataKey="temp_id"
                class="p-datatable-sm"
                :rowClass="getRowClass"
              >
                <Column style="width: 22%">
                  <template #header>
                    <InfoTooltip
                      :text="$t('go_cancel.tooltip_beneficiary_name')"
                    >
                      {{ $t("go_cancel.form.beneficiary_name.label") }}
                    </InfoTooltip>
                  </template>
                  <template #body="{ data }">
                    <BeneficiaryNameCell
                      v-model="data.beneficiary_name"
                      :suggestions="beneficiarySuggestions"
                      :touched="data.touched"
                      :disabled="isEditMwhOnly"
                      @select="applyBeneficiarySuggestion(data, $event)"
                      @touch="touchRow(data)"
                    />
                  </template>
                </Column>

                <Column style="width: 19%">
                  <template #header>
                    <InfoTooltip
                      :text="$t('go_cancel.tooltip_registry_country_region')"
                    >
                      {{ $t("go_cancel.form.registry_country_region.label") }}
                    </InfoTooltip>
                  </template>
                  <template #body="{ data }">
                    <select
                      v-model="data.registry_country_region"
                      class="form-select form-select-sm"
                      :class="{
                        'is-invalid':
                          data.touched && !data.registry_country_region?.trim(),
                      }"
                      :disabled="isEditMwhOnly"
                      @change="touchRow(data)"
                    >
                      <option disabled value="">
                        {{
                          $t(
                            "go_cancel.form.registry_country_region.placeholder"
                          )
                        }}
                      </option>
                      <option
                        v-for="registry in registries"
                        :key="registry.value"
                        :value="registry.value"
                      >
                        {{ registry.label }}
                      </option>
                    </select>
                  </template>
                </Column>

                <Column style="width: 14%">
                  <template #header>
                    <InfoTooltip :text="$t('go_cancel.tooltip_registry_code')">
                      {{ $t("go_cancel.form.registry_code.label") }}
                    </InfoTooltip>
                  </template>
                  <template #body="{ data }">
                    <input
                      v-model="data.registry_code"
                      type="text"
                      class="form-control form-control-sm"
                      :class="{
                        'is-invalid':
                          data.touched &&
                          !isAnyAibRow(data.registry_country_region) &&
                          !data.registry_code?.trim(),
                      }"
                      :placeholder="
                        $t('go_cancel.form.registry_code.placeholder')
                      "
                      :disabled="isEditMwhOnly"
                      @blur="touchRow(data)"
                    />
                  </template>
                </Column>

                <Column style="width: 25%">
                  <template #header>
                    <InfoTooltip :text="$t('go_cancel.tooltip_address')">
                      {{ $t("go_cancel.form.address.label") }}
                    </InfoTooltip>
                  </template>
                  <template #body="{ data }">
                    <input
                      v-model="data.address"
                      type="text"
                      class="form-control form-control-sm"
                      :class="{
                        'is-invalid':
                          data.touched &&
                          isAddressRequired(data.registry_country_region) &&
                          !data.address?.trim(),
                      }"
                      :placeholder="$t('go_cancel.form.address.placeholder')"
                      :disabled="isEditMwhOnly"
                      @blur="touchRow(data)"
                    />
                  </template>
                </Column>

                <Column style="width: 120px">
                  <template #header>
                    <InfoTooltip :text="$t('go_cancel.tooltip_mwh_cancelled')">
                      {{ $t("go_cancel.form.mwh_cancelled.label") }}
                    </InfoTooltip>
                  </template>
                  <template #body="{ data }">
                    <input
                      v-model.number="data.mwh_cancelled"
                      type="number"
                      min="1"
                      step="1"
                      class="form-control form-control-sm"
                      :class="{
                        'is-invalid':
                          data.touched &&
                          !isCancelRowMwhValid(data.mwh_cancelled),
                      }"
                      :placeholder="
                        $t('go_cancel.form.mwh_cancelled.placeholder')
                      "
                      @blur="touchRow(data)"
                    />
                  </template>
                </Column>

                <!-- Registry-specific options (DEV-583). The trigger also reads out the row's
                     privacy state, so no badge is needed in the region column. Rendered in edit-mwh
                     mode too, and deliberately left enabled there: privacy is the one field the
                     confirm route may still change. -->
                <Column style="width: 36px">
                  <template #body="{ data, index }">
                    <StatementOptionsMenu
                      v-if="
                        supportsPrivateCancellation(
                          data.registry_country_region
                        )
                      "
                      v-model="data.keep_statement_private"
                      :statementIndex="index"
                    />
                  </template>
                </Column>

                <Column v-if="!isEditMwhOnly" style="width: 36px">
                  <template #body="{ index }">
                    <button
                      class="btn btn-link btn-sm text-danger p-0"
                      :aria-label="
                        $t('go_cancel.remove_statement_row_aria', {
                          index: index + 1,
                        })
                      "
                      :disabled="cancelRows.length <= 1"
                      @click="removeCancelStatement(index)"
                    >
                      <FontAwesomeIcon icon="times" />
                    </button>
                  </template>
                </Column>
              </DataTable>
              <div v-else class="alert alert-info m-3">
                {{ $t("go_cancel.no_statements_yet") }}
              </div>
            </div>
            <ScrollMoreIndicator
              :scrollTarget="tableScrollRef"
              :itemHeight="56"
            >
              <template #default="{ count }">
                ~{{ count }} {{ $t("go_cancel.more_statements") }}
              </template>
            </ScrollMoreIndicator>
          </div>

          <div
            v-if="hasInvalidRows"
            class="validation-summary d-flex align-items-center gap-2 px-3 mb-0"
          >
            <FontAwesomeIcon
              icon="exclamation-triangle"
              class="flex-shrink-0"
            />
            <span class="small fw-medium">
              {{ $t("go_cancel.table_validation_hint") }}
            </span>
          </div>

          <!-- Shared settings sub-section -->
          <div v-if="!isEditMwhOnly" class="border-top p-3">
            <div class="eyebrow-label mb-3">
              {{ $t("go_cancel.shared_defaults_title") }}
            </div>

            <div
              v-if="props.hasInconsistentSharedFields"
              class="advisory-note mb-3"
            >
              <FontAwesomeIcon
                :icon="['far', 'circle-info']"
                class="flex-shrink-0 mt-1"
              />
              <div>
                {{
                  $t("go_cancel.analysis_shared_fields_inconsistent_warning")
                }}
              </div>
            </div>

            <div class="row g-3">
              <div class="col-md-2">
                <label class="form-label">
                  {{ $t("go_cancel.form.beneficiary_type.label") }}
                  <span class="text-danger">*</span>
                </label>
                <select
                  v-model="sharedDefaults.beneficiary_type"
                  class="form-select form-select-sm"
                  :disabled="isEditMwhOnly"
                  @change="handleSharedDefaultsChange"
                >
                  <option
                    v-for="beneficiaryType in allowedBeneficiaryTypes"
                    :key="beneficiaryType"
                    :value="beneficiaryType"
                  >
                    {{
                      $t(`go_cancel.form.beneficiary_type.${beneficiaryType}`)
                    }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">
                  {{ $t("go_cancel.form.period_start.label") }}
                  <span class="text-danger">*</span>
                </label>
                <input
                  v-model="sharedDefaults.period_start"
                  type="date"
                  class="form-control form-control-sm"
                  :class="{
                    'is-invalid':
                      sharedDefaultsTouched &&
                      (!sharedDefaults.period_start ||
                        isPeriodStartAfterCutoff),
                  }"
                  :disabled="isEditMwhOnly"
                  @change="handleSharedDefaultsChange"
                />
                <div
                  v-if="sharedDefaultsTouched && isPeriodStartAfterCutoff"
                  class="invalid-feedback"
                >
                  {{
                    $t("go_cancel.form.prior_year_cutoff_specific", {
                      year: periodStartCutoffYear,
                      cutoffDate: `31.03.${periodStartCutoffYear + 1}`,
                    })
                  }}
                </div>
              </div>
              <div class="col-md-3">
                <label class="form-label">
                  {{ $t("go_cancel.form.period_end.label") }}
                  <span class="text-danger">*</span>
                </label>
                <input
                  v-model="sharedDefaults.period_end"
                  type="date"
                  class="form-control form-control-sm"
                  :class="{
                    'is-invalid':
                      sharedDefaultsTouched &&
                      (isPeriodEndBeforeStart ||
                        !sharedDefaults.period_end ||
                        isPeriodEndAfterCutoff),
                  }"
                  :min="sharedDefaults.period_start || undefined"
                  :disabled="isEditMwhOnly"
                  @change="handleSharedDefaultsChange"
                />
                <div
                  v-if="
                    sharedDefaultsTouched &&
                    (isPeriodEndBeforeStart || isPeriodEndAfterCutoff)
                  "
                  class="invalid-feedback"
                >
                  <span v-if="isPeriodEndBeforeStart">
                    {{ $t("go_cancel.form.period_end.invalid_date_range") }}
                  </span>
                  <span v-else-if="isPeriodEndAfterCutoff">
                    {{
                      $t("go_cancel.form.prior_year_cutoff_specific", {
                        year: cutoffYear,
                        cutoffDate: `31.03.${cutoffNextYear}`,
                      })
                    }}
                  </span>
                </div>
                <div class="form-text">
                  {{
                    $t("go_cancel.form.period_end.cutoff_help", {
                      year: cutoffYear,
                      nextYear: cutoffNextYear,
                    })
                  }}
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label">
                  {{ $t("go_cancel.form.cancellation_purpose_text.label") }}
                  <span class="text-danger">*</span>
                </label>
                <input
                  v-model="sharedDefaults.cancellation_purpose_template"
                  type="text"
                  class="form-control form-control-sm"
                  :class="{
                    'is-invalid':
                      sharedDefaultsTouched &&
                      !sanitizeOperationNotesTemplate(
                        sharedDefaults.cancellation_purpose_template
                      ),
                  }"
                  :placeholder="
                    $t('go_cancel.form.cancellation_purpose_text.placeholder')
                  "
                  :disabled="isEditMwhOnly"
                  @input="handleSharedDefaultsChange"
                />
                <div class="form-text">
                  {{ $t("go_cancel.form.cancellation_purpose_text.help") }}
                </div>
                <div
                  v-if="hasAddressPlaceholderWithoutAddress"
                  class="error-note mt-2 mb-0"
                >
                  <FontAwesomeIcon
                    icon="exclamation-triangle"
                    class="flex-shrink-0"
                  />
                  <span>{{
                    $t("go_cancel.address_placeholder_missing_warning")
                  }}</span>
                </div>
              </div>
            </div>

            <BeneficiaryTypeConflictNotice
              v-if="beneficiaryTypeConflicts.length > 0"
              class="mt-3 mb-0"
              :conflicts="beneficiaryTypeConflicts"
              :sharedType="sharedDefaults.beneficiary_type"
              :switchableType="switchableBeneficiaryType"
            />

            <div v-if="showSharedDefaultsWarning" class="error-note mt-3 mb-0">
              <FontAwesomeIcon
                icon="exclamation-triangle"
                class="flex-shrink-0"
              />
              <span>{{ $t("go_cancel.shared_defaults_incomplete") }}</span>
            </div>
          </div>

          <!-- Overall notes sub-section -->
          <div v-if="!isEditMwhOnly" class="border-top p-3">
            <div class="eyebrow-label mb-2">
              {{ $t("go_cancel.form.global_operation_notes.label") }}
            </div>
            <textarea
              v-model="globalOperationNotes"
              class="form-control"
              :placeholder="
                $t('go_cancel.form.global_operation_notes.placeholder')
              "
              rows="3"
            ></textarea>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import ScrollMoreIndicator from "@/components/common/ScrollMoreIndicator.vue";
import {
  ANY_AIB_REGISTRY_SENTINEL,
  SUPPORTED_REGISTRIES_WITH_LABELS,
} from "@/constants";
import BeneficiaryNameCell from "@/dashboard/inventory_management/cancel/BeneficiaryNameCell.vue";
import type { CancelBeneficiarySuggestion } from "@/dashboard/inventory_management/cancel/cancelBeneficiariesApi";
import BeneficiaryTypeConflictNotice from "@/dashboard/inventory_management/cancel/BeneficiaryTypeConflictNotice.vue";
import RegionMismatchAdvisory from "@/dashboard/inventory_management/cancel/RegionMismatchAdvisory.vue";
import StatementOptionsMenu from "@/dashboard/inventory_management/cancel/StatementOptionsMenu.vue";
import { useCancelEditorAdvisories } from "@/dashboard/inventory_management/cancel/useCancelEditorAdvisories";
import UnsupportedRegistryNotice from "@/dashboard/inventory_management/common/UnsupportedRegistryNotice.vue";
import { createEmptyCancelStatement } from "@/dashboard/inventory_management/common/cancelAiMapping";
import {
  type CancelSharedDefaults,
  MAX_CANCEL_STATEMENTS,
  buildCancelStatementPayload,
  getCancelRegionLabel,
  getDefaultCancelPeriodRange,
  getDefaultOperationNotesTemplate,
  isAnyAibRow,
  isCancelRowMwhValid,
  isCancelRowValid,
  isCancelSharedDefaultsValid,
  refreshOperationNotesTemplate,
  resolveDefaultRegistryRegion,
  sanitizeOperationNotesTemplate,
  withStatementPrivacy,
} from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import {
  getRegistryRule,
  isPriorYearPeriodAllowed,
  supportsPrivateCancellation,
} from "@/dashboard/inventory_management/common/cancellationRegistryRules";
import type { IndividualCancelStatement } from "@/types";
import { BeneficiaryTypeEnum } from "@/types";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

interface CompactCancelRow {
  temp_id: string;
  beneficiary_name: string;
  registry_country_region: string;
  registry_code: string | null;
  address: string | null;
  mwh_cancelled: number | null;
  // DEV-583. Row-local UI state. Deliberately NOT cleared when the region changes: emission is
  // region-gated, so LV -> EE -> LV resurfaces the earlier choice instead of losing it silently.
  keep_statement_private: boolean;
  touched: boolean;
  // Present on rows hydrated from a saved statement; carries the full saved
  // payload so non-editor fields (cancellation_purpose, future fields)
  // round-trip untouched.
  original_statement?: IndividualCancelStatement;
}

type EditorMode = "create" | "edit-mwh";

const props = withDefaults(
  defineProps<{
    initialRows?: CompactCancelRow[];
    initialSharedDefaults?: CancelSharedDefaults;
    initialGlobalOperationNotes?: string | null;
    selectedRegions?: string[];
    truncatedWarning?: string | null;
    hasInconsistentSharedFields?: boolean;
    mode?: EditorMode;
    beneficiarySuggestions?: CancelBeneficiarySuggestion[];
  }>(),
  {
    initialRows: () => [],
    initialSharedDefaults: undefined,
    initialGlobalOperationNotes: null,
    selectedRegions: () => [],
    truncatedWarning: null,
    hasInconsistentSharedFields: false,
    mode: "create",
    beneficiarySuggestions: () => [],
  }
);

const emit = defineEmits<{
  "shared-defaults:edited": [];
}>();

const isEditMwhOnly = computed(() => props.mode === "edit-mwh");

const { t } = useI18n();

/**
 * Picking a saved beneficiary is an explicit act, so it overwrites the row's beneficiary fields —
 * including with the empty registry code / address of a beneficiary last used without them. The
 * region is the exception: a null one means the history holds no region this form can offer, which
 * is no reason to discard a region the user has already chosen.
 *
 * The remembered type is adopted into the shared defaults (every statement submits the one shared
 * type), which counts as editing them — hence handleSharedDefaultsChange rather than writing the ref
 * behind the parent's back. A type that is unknown, unofferable (CONVERSION_PLANT_OPERATOR would
 * render a blank select that still submits), or contradicted by another row is left to the conflict
 * advisory instead, so the earlier pick wins.
 */
const applyBeneficiarySuggestion = (
  row: CompactCancelRow,
  suggestion: CancelBeneficiarySuggestion
) => {
  row.beneficiary_name = suggestion.beneficiary_name;
  row.registry_code = suggestion.registry_code;
  row.address = suggestion.address;
  if (suggestion.registry_country_region) {
    row.registry_country_region = suggestion.registry_country_region;
  }
  row.touched = true;
  const rememberedType = suggestion.beneficiary_type;
  // Guarded after the assignments above, so the picked row already agrees and only other rows veto.
  if (
    rememberedType &&
    allowedBeneficiaryTypes.includes(rememberedType) &&
    beneficiaryTypeConflictsWith(rememberedType).length === 0
  ) {
    sharedDefaults.value.beneficiary_type = rememberedType;
    handleSharedDefaultsChange();
  }
};

const createInitialSharedDefaults = (): CancelSharedDefaults => {
  if (props.initialSharedDefaults) {
    return { ...props.initialSharedDefaults };
  }
  const defaultRange = getDefaultCancelPeriodRange();
  return {
    beneficiary_type: BeneficiaryTypeEnum.CONSUMER,
    period_start: defaultRange.start,
    period_end: defaultRange.end,
    cancellation_purpose_template: getDefaultOperationNotesTemplate(
      BeneficiaryTypeEnum.CONSUMER
    ),
  };
};

const sharedDefaults = ref<CancelSharedDefaults>(createInitialSharedDefaults());
// In edit-mwh mode the period/notes inputs are disabled, so the user can
// never `touch` them. Start touched so existing-but-invalid values (e.g. a
// period that has fallen past the prior-year cutoff) surface their validation
// feedback immediately rather than silently disabling the parent's save button.
const sharedDefaultsTouched = ref(isEditMwhOnly.value);
const cancelRows = ref<CompactCancelRow[]>(
  props.initialRows.map((r) => ({ ...r }))
);
const globalOperationNotes = ref<string | null>(
  props.initialGlobalOperationNotes ?? null
);
const tableScrollRef = useTemplateRef<HTMLElement>("tableScrollRef");

const allowedBeneficiaryTypes = [
  BeneficiaryTypeEnum.CONSUMER,
  BeneficiaryTypeEnum.ELECTRICITY_SELLER,
];

const registries = computed(() =>
  [
    ...SUPPORTED_REGISTRIES_WITH_LABELS.map((registry) => registry.value),
    ANY_AIB_REGISTRY_SENTINEL,
  ].map((value) => ({ value, label: getCancelRegionLabel(value, t) }))
);

const isAtStatementLimit = computed(
  () => cancelRows.value.length >= MAX_CANCEL_STATEMENTS
);

const selectedRegions = computed(() => props.selectedRegions ?? []);

// Non-blocking advisories (region mismatch, unsupported-registry notice, Any AIB address hint /
// missing-address warning, remembered beneficiary-type conflict) live in a composable so the warning
// logic stays cohesive and testable.
const {
  hasRegionMismatch,
  hasUnsupportedRegistry,
  unsupportedRegistryEntries,
  shouldSuggestAddressPlaceholder,
  hasAddressPlaceholderWithoutAddress,
  beneficiaryTypeConflicts,
  beneficiaryTypeConflictsWith,
} = useCancelEditorAdvisories({
  rows: cancelRows,
  materializedStatements: () => materializedStatements.value,
  purposeTemplate: () => sharedDefaults.value.cancellation_purpose_template,
  selectedRegions,
  beneficiarySuggestions: () => props.beneficiarySuggestions,
  sharedBeneficiaryType: () => sharedDefaults.value.beneficiary_type,
});

// Advising a switch only helps when some offerable type leaves no conflict at all. Null when none
// does, and the notice then prescribes nothing.
const switchableBeneficiaryType = computed(
  () =>
    allowedBeneficiaryTypes.find(
      (candidate) => beneficiaryTypeConflictsWith(candidate).length === 0
    ) ?? null
);

const isStatementValid = (statement: CompactCancelRow): boolean => {
  return isCancelRowValid({
    beneficiary_name: statement.beneficiary_name,
    registry_code: statement.registry_code,
    registry_country_region: statement.registry_country_region,
    address: statement.address,
    mwh_cancelled: statement.mwh_cancelled,
  });
};

const allStatementsValid = computed(() => {
  if (cancelRows.value.length === 0) {
    return false;
  }
  return cancelRows.value.every(isStatementValid);
});

const hasInvalidRows = computed(() =>
  cancelRows.value.some(
    (statement) => statement.touched && !isStatementValid(statement)
  )
);

const isSharedDefaultsValid = computed(() =>
  isCancelSharedDefaultsValid(sharedDefaults.value)
);

const isPeriodEndAfterCutoff = computed(() => {
  const { period_end } = sharedDefaults.value;
  if (!period_end) return false;
  return !isPriorYearPeriodAllowed(new Date(), period_end);
});

const isPeriodStartAfterCutoff = computed(() => {
  const { period_start } = sharedDefaults.value;
  if (!period_start) return false;
  return !isPriorYearPeriodAllowed(new Date(), period_start);
});

const isPeriodEndBeforeStart = computed(() => {
  const { period_start, period_end } = sharedDefaults.value;
  if (!period_start || !period_end) return false;
  return period_end < period_start;
});

const yearFromDateString = (value?: string): number =>
  value ? Number(value.slice(0, 4)) : new Date().getFullYear();

const periodStartCutoffYear = computed(() =>
  yearFromDateString(sharedDefaults.value.period_start)
);

const cutoffYear = computed(() =>
  yearFromDateString(sharedDefaults.value.period_end)
);

const cutoffNextYear = computed(() => cutoffYear.value + 1);

const showSharedDefaultsWarning = computed(
  () => sharedDefaultsTouched.value && !isSharedDefaultsValid.value
);

const materializedStatements = computed<IndividualCancelStatement[]>(() =>
  cancelRows.value.map((row) => {
    if (isEditMwhOnly.value) {
      // edit-mwh mode hides every input except mwh_cancelled, so preserve the
      // saved statement verbatim and overlay only the edited MWh. The parent
      // is required to supply original_statement on every row in this mode.
      if (!row.original_statement) {
        throw new Error(
          "CancelStatementsEditor: edit-mwh mode requires original_statement on every row"
        );
      }
      return {
        ...row.original_statement,
        mwh_cancelled:
          row.mwh_cancelled === null
            ? null
            : Math.floor(Number(row.mwh_cancelled)),
        // Explicit override, ungated on region (unlike the create branch): untoggling must clear a
        // stored flag, and a non-Grexel PPA statement's stored choice must survive an MWh-only edit.
        grexel_options: withStatementPrivacy(row.keep_statement_private),
      };
    }
    return buildCancelStatementPayload(
      {
        temp_id: row.temp_id,
        beneficiary_name: row.beneficiary_name,
        registry_code: row.registry_code,
        registry_country_region: row.registry_country_region,
        address: row.address,
        mwh_cancelled: row.mwh_cancelled,
        keep_statement_private: row.keep_statement_private,
      },
      sharedDefaults.value
    );
  })
);

const totalMwhToCancel = computed(() =>
  cancelRows.value.reduce((total, row) => {
    const mwh = Number(row.mwh_cancelled);
    return total + (isNaN(mwh) || mwh < 0 ? 0 : Math.floor(mwh));
  }, 0)
);

const isValid = computed(
  () =>
    cancelRows.value.length > 0 &&
    allStatementsValid.value &&
    (isEditMwhOnly.value || isSharedDefaultsValid.value) &&
    totalMwhToCancel.value > 0
);

const createNewRow = (): CompactCancelRow => {
  const statement = createEmptyCancelStatement();
  const defaultRegistryRegion = resolveDefaultRegistryRegion(
    statement.registry_country_region,
    selectedRegions.value
  );
  return {
    temp_id: statement.temp_id ?? "",
    beneficiary_name: statement.beneficiary_name,
    registry_country_region:
      defaultRegistryRegion ?? statement.registry_country_region ?? "",
    registry_code: statement.registry_code ?? null,
    address: statement.address ?? null,
    mwh_cancelled: statement.mwh_cancelled ?? null,
    keep_statement_private: false,
    touched: false,
  };
};

const addCancelStatement = () => {
  if (isAtStatementLimit.value) return;
  cancelRows.value.push(createNewRow());
};

const removeCancelStatement = (index: number) => {
  cancelRows.value.splice(index, 1);
  if (cancelRows.value.length === 0) {
    addCancelStatement();
  }
};

const touchRow = (row: CompactCancelRow) => {
  row.touched = true;
};

const isAddressRequired = (registryCountryRegion?: string): boolean => {
  if (!registryCountryRegion) {
    return false;
  }
  return getRegistryRule(registryCountryRegion).requiresAddress;
};

const getRowClass = (row: CompactCancelRow): string =>
  row.touched && !isStatementValid(row) ? "cancel-row-invalid" : "";

const handleSharedDefaultsChange = () => {
  sharedDefaultsTouched.value = true;
  emit("shared-defaults:edited");
};

const touchAll = () => {
  sharedDefaultsTouched.value = true;
  cancelRows.value.forEach((row) => {
    row.touched = true;
  });
};

const setRows = (
  rows: CompactCancelRow[],
  newSharedDefaults?: CancelSharedDefaults,
  newGlobalNotes?: string | null
) => {
  cancelRows.value = rows.map((r) => ({ ...r }));
  if (newSharedDefaults) {
    sharedDefaults.value = { ...newSharedDefaults };
  }
  if (newGlobalNotes !== undefined) {
    globalOperationNotes.value = newGlobalNotes;
  }
};

const reset = () => {
  cancelRows.value = [];
  sharedDefaults.value = createInitialSharedDefaults();
  sharedDefaultsTouched.value = false;
  globalOperationNotes.value = null;
  addCancelStatement();
};

// Leaves `touched` alone: an amount the parent fills in must not light up the row's other, still
// empty fields as invalid before the operator has edited them.
const setStatementMwh = (index: number, mwh: number) => {
  const row = cancelRows.value[index];
  if (row) row.mwh_cancelled = mwh;
};

watch(
  () => [...props.selectedRegions],
  (regions) => {
    if (isEditMwhOnly.value) return;
    cancelRows.value.forEach((row) => {
      const defaultRegistry = resolveDefaultRegistryRegion(
        row.registry_country_region,
        regions
      );
      if (defaultRegistry) {
        row.registry_country_region = defaultRegistry;
      }
    });
  },
  { deep: true }
);

watch(
  () => sharedDefaults.value.beneficiary_type,
  (newType, oldType) => {
    sharedDefaults.value.cancellation_purpose_template =
      refreshOperationNotesTemplate(
        sharedDefaults.value.cancellation_purpose_template,
        newType,
        oldType
      );
  }
);

if (cancelRows.value.length === 0) {
  addCancelStatement();
}

defineExpose({
  materializedStatements,
  globalOperationNotes,
  sharedDefaults,
  cancelRows,
  hasRegionMismatch,
  hasUnsupportedRegistry,
  isValid,
  totalMwhToCancel,
  touchAll,
  setRows,
  reset,
  addCancelStatement,
  setStatementMwh,
});
</script>

<style scoped lang="scss">
.compact-table-container {
  max-height: 460px;
  overflow-y: auto;

  :deep(.pv-datatable-header-cell),
  :deep(.pv-datatable-body-cell) {
    white-space: normal;
    overflow: visible;
    // Roomier rows than the app-wide compact default (~6px vertical) — matches
    // the cancellation redesign reference.
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }

  // Column headers as quiet uppercase eyebrow labels (redesign). The
  // InfoTooltip label text inherits these; its "?" icon keeps its muted color.
  :deep(.pv-datatable-header-cell) {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--phoenix-secondary-color);
  }

  // Invalid (touched) rows get a faint pink wash — a lighter tint than the
  // validation band below, matching the redesign. Held through hover so the
  // row stays visibly flagged while the user fixes it.
  :deep(.pv-datatable-body-row.cancel-row-invalid),
  :deep(.pv-datatable-body-row.cancel-row-invalid:hover) {
    background: color-mix(
      in srgb,
      var(--phoenix-danger-bg-subtle) 30%,
      var(--phoenix-emphasis-bg)
    );
  }
}

.cancel-statements-card {
  overflow: hidden;
}

// Validation summary anchoring the table foot: a calm red wash + top hairline,
// replacing the old solid-ochre alert. Shares the danger-subtle language of the
// filter shortfall line under the portfolio table's chips — errors are red, advisories are ochre.
.validation-summary {
  background: var(--phoenix-danger-bg-subtle);
  border-top: 1px solid var(--phoenix-danger-border-subtle);
  color: var(--phoenix-danger-text-emphasis);
  // Roomier vertical breathing room than py-2 — matches the design's band.
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}
</style>
